import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { SnackbarContainer } from 'components/standalone';
import { extractErrorMessage } from 'utils';

import { ContractorForm } from '../components';
import { ContractorContainer, ContractorFormValues } from '../containers';
import { getMissingPaymentTypeMessage } from '../resource-types';
import { buildContractorPayload } from './contractorPayload';

const ContractorsCreateInner = () => {
  const classes = useStyles();
  const history = useHistory();
  const { notify } = SnackbarContainer.useContainer();
  const {
    formLoading,
    createContractor,
    loadRegionOptions,
    loadBumperOptions,
    setFormLoading,
    regionsError,
  } = ContractorContainer.useContainer();

  const handleSubmit = useCallback(
    async (values: ContractorFormValues) => {
      // Guarded toggle (e.g. Bumper) ON without its payment type → block & warn.
      const missingPaymentMessage = getMissingPaymentTypeMessage(
        values as unknown as Record<string, unknown>,
      );
      if (missingPaymentMessage) {
        notify(missingPaymentMessage, 'error');
        return;
      }

      setFormLoading(true);
      try {
        // Single transactional request: contractor + regions.
        const created = await createContractor(
          buildContractorPayload(values),
          values.selectedRegions,
        );

        notify('Element created');
        setFormLoading(false);

        if (created.bumperAvailable) {
          history.push(`/contractor/${created.id}`);
        } else {
          history.push('/contractor');
        }
      } catch (error: unknown) {
        console.error('Contractor create failed:', error);
        notify(
          extractErrorMessage(error, 'Error while creating the contractor'),
          'error',
        );
        setFormLoading(false);
      }
    },
    [createContractor, history, notify, setFormLoading],
  );

  const handleGoBack = useCallback(() => {
    history.push('/contractor');
  }, [history]);

  return (
    <Box className={classes.formContainer}>
      <ContractorForm
        mode="create"
        onSubmit={handleSubmit}
        loading={formLoading}
        onGoBack={handleGoBack}
        loadRegionOptions={loadRegionOptions}
        loadBumperOptions={loadBumperOptions}
        regionsError={regionsError}
      />
    </Box>
  );
};

export const ContractorsCreate = () => (
  <ContractorContainer.Provider>
    <ContractorsCreateInner />
  </ContractorContainer.Provider>
);

const useStyles = makeStyles(theme => ({
  formContainer: {
    position: 'relative',
    boxShadow: theme.shadows[2],
    backgroundColor: theme.palette.background.paper,
  },
}));

export default ContractorsCreate;
